/**
 * Turns completed tool results into citable sources.
 *
 * Extractors are keyed by tool key and read *named fields only* — never a deep
 * scan for anything URL-shaped. A generic sweep would drag in API endpoints,
 * avatar images and tracking pixels, and the scraped-page outputs are large
 * enough that walking them wholesale on every stream tick is not free.
 *
 * Kept free of React/DOM so it can be unit tested in isolation (see
 * citation-extractors.spec.ts).
 */

import { coco, ResourceType } from '@cotera/api';
import type { MessageClass, ToolPart } from '@cotera/sdk/core';
import {
  cleanString,
  getHostname,
} from '../tool-renderers/web-tool-renderer-utils';
import {
  normalizeCitationUrl,
  resourceCitationKey,
  type CitationSource,
} from './citation-source';

const {
  COCO_WEB_SEARCH_TOOL_KEY,
  COCO_SITE_SCRAPER_TOOL_KEY,
  COCO_SEARCH_TOOL_KEY,
} = coco;

/** Derives a source from one completed tool part. */
export type CitationExtractor = (part: ToolPart) => CitationSource[];

const CITATION_EXTRACTORS: Record<string, CitationExtractor> = {};

/**
 * Register an extractor for a tool key. Mirrors `registerToolRenderer` in
 * ../tool-renderers/index.tsx — module-load side effect, exact key match.
 */
export function registerCitationExtractor(
  toolKey: string,
  extractor: CitationExtractor
): void {
  CITATION_EXTRACTORS[toolKey] = extractor;
}

export function getCitationExtractor(
  toolKey: string
): CitationExtractor | null {
  return CITATION_EXTRACTORS[toolKey] ?? null;
}

/** Build a web source, or nothing when the URL has no derivable host. */
function webSource(rawUrl: unknown, rawTitle?: unknown): CitationSource[] {
  const url = cleanString(rawUrl);
  if (url === undefined) {
    return [];
  }
  const siteLabel = getHostname(url);
  if (siteLabel === undefined) {
    return [];
  }
  const title = cleanString(rawTitle);
  return [
    { kind: 'web', url, siteLabel, ...(title !== undefined && { title }) },
  ];
}

type WebSearchOutput = {
  results?: { link?: string | null; title?: string | null }[] | null;
};

type SiteScraperOutput = {
  url?: string | null;
  title?: string | null;
};

type TavilySearchOutput = {
  results?: { url?: string | null; title?: string | null }[] | null;
};

type PerplexityOutput = {
  citations?: string[] | null;
};

/** Resource kinds `coco/search` returns that map onto a citable page. */
const SEARCH_RESOURCE_TYPES: Record<
  string,
  Exclude<ResourceType, ResourceType.Folder>
> = {
  chat: ResourceType.Chat,
  agent: ResourceType.Agent,
  document: ResourceType.Document,
  workflow: ResourceType.Workflow,
  workflow_trigger: ResourceType.WorkflowTrigger,
};

type SearchOutput = {
  items?:
    | {
        t?: string;
        data?: { id?: string; title?: string | null; name?: string | null };
      }[]
    | null;
};

registerCitationExtractor(COCO_WEB_SEARCH_TOOL_KEY, (part) =>
  (part.getOutput<WebSearchOutput>()?.results ?? []).flatMap((result) =>
    result === null || result === undefined
      ? []
      : webSource(result.link, result.title)
  )
);

registerCitationExtractor(COCO_SITE_SCRAPER_TOOL_KEY, (part) => {
  const output = part.getOutput<SiteScraperOutput>();
  // Falls back to the requested URL: the scraper echoes it, but a partial
  // result may only carry the input.
  return webSource(output?.url ?? part.input['url'], output?.title);
});

registerCitationExtractor('tavily/search', (part) =>
  (part.getOutput<TavilySearchOutput>()?.results ?? []).flatMap((result) =>
    result === null || result === undefined
      ? []
      : webSource(result.url, result.title)
  )
);

registerCitationExtractor('perplexity/chat/completions', (part) =>
  (part.getOutput<PerplexityOutput>()?.citations ?? []).flatMap((url) =>
    webSource(url)
  )
);

registerCitationExtractor(COCO_SEARCH_TOOL_KEY, (part) =>
  (part.getOutput<SearchOutput>()?.items ?? []).flatMap((item) => {
    if (item === null || item === undefined) {
      return [];
    }
    const resourceType =
      item.t !== undefined ? SEARCH_RESOURCE_TYPES[item.t] : undefined;
    const resourceId = cleanString(item.data?.id);
    if (resourceType === undefined || resourceId === undefined) {
      return [];
    }
    const title = cleanString(item.data?.title) ?? cleanString(item.data?.name);
    return [
      {
        kind: 'resource' as const,
        // The rendered href comes from the model's own link; this is only a
        // sensible default for chips that resolve without one.
        url: resourceId,
        siteLabel: title ?? 'Workspace',
        resourceType,
        resourceId,
        ...(title !== undefined && { title }),
      },
    ];
  })
);

/** Registry key for a source, matching how hrefs are keyed at lookup time. */
function keyForSource(source: CitationSource): string | undefined {
  return source.kind === 'resource'
    ? resourceCitationKey(source.resourceId)
    : normalizeCitationUrl(source.url);
}

/**
 * Per-tool-call extraction cache.
 *
 * `messages` gets a new identity on every stream tick, so rebuilding the
 * registry from scratch would re-walk every tool output — including full
 * scraped pages — many times a second. Keyed by tool call plus state so a
 * completed call is extracted exactly once.
 */
export type CitationExtractionCache = Map<string, CitationSource[]>;

export function createCitationExtractionCache(): CitationExtractionCache {
  return new Map();
}

function extractFromPart(
  part: ToolPart,
  cache: CitationExtractionCache
): CitationSource[] {
  let toolKey: string;
  try {
    toolKey = part.toolKey().toString();
  } catch {
    // Malformed/partial tool names shouldn't break rendering.
    return [];
  }
  const extractor = getCitationExtractor(toolKey);
  if (extractor === null) {
    return [];
  }

  const cacheKey = `${part.toolCallId}:${part.raw().state}`;
  const cached = cache.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }

  let sources: CitationSource[];
  try {
    sources = extractor(part);
  } catch {
    // Persisted tool payloads can be partially malformed.
    sources = [];
  }
  cache.set(cacheKey, sources);
  return sources;
}

/**
 * Index every citable source in the conversation by lookup key.
 *
 * Built across *all* messages rather than per message: Coco's step loop
 * routinely puts the tool call and the prose that cites it in different
 * assistant messages, and it lets an answer cite a source found several turns
 * earlier. First writer wins, so the earliest tool result keeps its title.
 */
export function buildCitationRegistry(
  messages: readonly MessageClass[],
  cache: CitationExtractionCache = createCitationExtractionCache()
): ReadonlyMap<string, CitationSource> {
  const registry = new Map<string, CitationSource>();
  for (const message of messages) {
    for (const part of message.parts) {
      if (!part.isToolPart()) {
        continue;
      }
      for (const source of extractFromPart(part, cache)) {
        const key = keyForSource(source);
        if (key !== undefined && !registry.has(key)) {
          registry.set(key, source);
        }
      }
    }
  }
  return registry;
}
